import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    fetchCampaigns, fetchSpends, applySpend,
    updateSpend, deleteSpend, clearSpendRange,
} from '../../api';
import DataGrid from '../common/DataGrid';
import DateRangePicker from '../common/DateRangePicker';
import Modal from './Modal';
import { actionsColumn, formatMoney, moneyColumn, monthRange } from './shared';
import '../reports/Reports.css';
import './Marketing.css';

// Spend entry. A spend is stored one row per campaign per day, but it's entered
// across a date range: pick a campaign and a range, say whether the amount is
// the total for the range or a per-day figure, and whether it's actual or
// estimated spend. Days that already have a row are updated rather than
// duplicated, and only the chosen column is touched — so entering a month of
// estimates and later filling in actuals leaves the estimates intact.

// Parses YYYY-MM-DD as a local date. (new Date("2026-08-01") would parse as UTC
// midnight and land on July 31 in timezones behind UTC.)
function parseISODate(value) {
    const [y, m, d] = String(value || '').split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}

// Mirrors the server's expandDates: every day in the range, weekends optionally
// dropped. Duplicated here so the form can preview the split before saving.
function expandDates(start, end, skipWeekends) {
    const dates = [];
    if (!start || !end || end < start) return dates;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const day = d.getDay();
        if (skipWeekends && (day === 0 || day === 6)) continue;
        dates.push(new Date(d));
    }
    return dates;
}

// Mirrors the server's splitCents: as-equal-as-possible parts that sum to the
// exact total, with the leftover pennies on the last day.
function splitCents(total, n) {
    if (n <= 0) return [];
    const base = Math.floor(total / n);
    const parts = new Array(n).fill(base);
    parts[n - 1] += total - base * n;
    return parts;
}

const toCents = (amount) => Math.round(amount * 100);

// Rows the ad spend sync wrote carry the platform in `source`; hand-entered
// rows have none.
const SOURCE_LABEL = { google_ads: 'Google Ads', meta: 'Meta' };

const formatSyncedAt = (value) => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

function SourcePill({ value, node }) {
    if (node && node.rowPinned) return null;
    if (!value) return <span className="mkt__source mkt__source--manual">Manual</span>;
    return <span className="mkt__source">{SOURCE_LABEL[value] || value}</span>;
}

const EMPTY_ENTRY = {
    campaignId: '',
    skipWeekends: false,
    amount: '',
    mode: 'total',
    field: 'amount',
};

export default function Spends() {
    const [campaigns, setCampaigns] = useState([]);
    const [spends, setSpends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    // Spend-entry form (the top panel), defaulting to the current month.
    const [entry, setEntry] = useState(EMPTY_ENTRY);
    const [entryRange, setEntryRange] = useState(monthRange);
    const [applying, setApplying] = useState(false);

    // The grid's own filter, independent of the entry form's range.
    const [filter, setFilter] = useState(() => ({ ...monthRange(), campaignId: '' }));

    const [dialog, setDialog] = useState(null);
    const [editForm, setEditForm] = useState({ amount: '', estimated: '' });
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);

    const loadSpends = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchSpends({
                startDate: filter.startDate,
                endDate: filter.endDate,
                campaignId: filter.campaignId || undefined,
            });
            setSpends(data || []);
        } catch (err) {
            setError(err.message);
            setSpends([]);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchCampaigns().then((data) => setCampaigns(data || [])).catch((err) => setError(err.message));
    }, []);

    useEffect(() => { loadSpends(); }, [loadSpends]);

    // Campaigns arrive ordered by vendor, so grouping them for <optgroup> is a
    // straight walk over the list.
    const campaignGroups = useMemo(() => {
        const groups = [];
        for (const c of campaigns) {
            const last = groups[groups.length - 1];
            if (last && last.vendor === c.vendor_name) last.campaigns.push(c);
            else groups.push({ vendor: c.vendor_name, campaigns: [c] });
        }
        return groups;
    }, [campaigns]);

    // Live readout of what Apply will write.
    const preview = useMemo(() => {
        const start = parseISODate(entryRange.startDate);
        const end = parseISODate(entryRange.endDate);
        if (!start || !end) return { error: 'Enter a start and end date.' };
        if (end < start) return { error: "The end date can't be before the start date." };

        const days = expandDates(start, end, entry.skipWeekends).length;
        if (days === 0) {
            return { error: entry.skipWeekends ? 'That range has no weekdays in it.' : 'That range has no days in it.' };
        }

        const amount = parseFloat(entry.amount);
        if (entry.amount === '' || Number.isNaN(amount)) return { days, error: null, amountMissing: true };
        if (amount < 0) return { days, error: "The amount can't be negative." };

        const cents = entry.mode === 'per_day'
            ? new Array(days).fill(toCents(amount))
            : splitCents(toCents(amount), days);
        const total = cents.reduce((sum, c) => sum + c, 0);
        // An uneven split leaves the last day a penny or two off the rest.
        const uneven = entry.mode === 'total' && cents[0] !== cents[days - 1];

        return {
            days,
            perDay: cents[0] / 100,
            lastDay: cents[days - 1] / 100,
            total: total / 100,
            uneven,
            error: null,
        };
    }, [entryRange, entry.skipWeekends, entry.amount, entry.mode]);

    const canApply = !applying && entry.campaignId !== '' && !preview.error && !preview.amountMissing;

    const handleApply = async () => {
        setApplying(true);
        setError(null);
        setNotice(null);
        try {
            const result = await applySpend({
                campaign_id: Number(entry.campaignId),
                start_date: entryRange.startDate,
                end_date: entryRange.endDate,
                skip_weekends: entry.skipWeekends,
                field: entry.field,
                mode: entry.mode,
                amount: parseFloat(entry.amount),
            });
            const label = entry.field === 'amount' ? 'actual spend' : 'estimated spend';
            setNotice(
                `Applied ${formatMoney(result.total)} of ${label} across ${result.days} day${result.days === 1 ? '' : 's'}.`
            );
            setEntry((prev) => ({ ...prev, amount: '' }));
            // Show the range that was just written.
            setFilter({ ...entryRange, campaignId: entry.campaignId });
        } catch (err) {
            setError(err.message);
        } finally {
            setApplying(false);
        }
    };

    const openEdit = useCallback((spend) => {
        setEditForm({
            amount: spend.amount == null ? '' : String(spend.amount),
            estimated: spend.estimated_amount == null ? '' : String(spend.estimated_amount),
        });
        setDialogError(null);
        setDialog({ mode: 'edit', spend });
    }, []);

    const openDelete = useCallback((spend) => {
        setDialogError(null);
        setDialog({ mode: 'delete', spend });
    }, []);

    const closeDialog = () => { setDialog(null); setDialogError(null); };

    const handleDialogSubmit = async () => {
        setSaving(true);
        setDialogError(null);
        try {
            if (dialog.mode === 'edit') {
                // A blank field clears that column rather than storing zero.
                const num = (v) => (v.trim() === '' ? null : Number(v));
                await updateSpend(dialog.spend.id, {
                    amount: num(editForm.amount),
                    estimated_amount: num(editForm.estimated),
                });
            } else if (dialog.mode === 'delete') {
                await deleteSpend(dialog.spend.id);
            } else if (dialog.mode === 'clear') {
                const result = await clearSpendRange({
                    campaignId: Number(entry.campaignId),
                    startDate: entryRange.startDate,
                    endDate: entryRange.endDate,
                });
                setNotice(`Cleared ${result.deleted} spend day${result.deleted === 1 ? '' : 's'}.`);
            }
            setDialog(null);
            await loadSpends();
        } catch (err) {
            setDialogError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const columnDefs = useMemo(() => [
        // cellDataType off because the pinned total row puts the word "Total"
        // in this column, and AG Grid renders a string in an inferred-date/number
        // column as "Invalid Number".
        { field: 'spend_date', headerName: 'Date', width: 130, cellDataType: false },
        { field: 'vendor_name', headerName: 'Vendor', width: 160 },
        { field: 'campaign_name', headerName: 'Campaign', flex: 1, minWidth: 180 },
        {
            field: 'source',
            headerName: 'Actual From',
            width: 140,
            cellRenderer: SourcePill,
            // Group/filter by the readable name rather than the key.
            valueFormatter: (p) => (p.node?.rowPinned ? '' : SOURCE_LABEL[p.value] || (p.value ? p.value : 'Manual')),
            filterValueGetter: (p) => SOURCE_LABEL[p.data?.source] || 'Manual',
            tooltipValueGetter: (p) => (p.data?.synced_at ? `Synced ${formatSyncedAt(p.data.synced_at)}` : null),
        },
        moneyColumn('amount', 'Actual'),
        moneyColumn('estimated_amount', 'Estimated'),
        actionsColumn(openEdit, openDelete),
    ], [openEdit, openDelete]);

    const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true, minWidth: 120 }), []);

    const selectedCampaign = campaigns.find((c) => String(c.id) === String(entry.campaignId));

    const totals = useMemo(() => spends.reduce((acc, s) => ({
        actual: acc.actual + Number(s.amount || 0),
        estimated: acc.estimated + Number(s.estimated_amount || 0),
    }), { actual: 0, estimated: 0 }), [spends]);

    // Pinned total row under the grid, labelled in the leading (date) column.
    const pinnedBottomRowData = useMemo(() => (
        spends.length
            ? [{ spend_date: 'Total', amount: totals.actual, estimated_amount: totals.estimated }]
            : undefined
    ), [spends.length, totals]);

    return (
        <div className="page">
            <div className="mkt__header">
                <div>
                    <h1 className="page__title">Spends</h1>
                    <p className="page__subtitle">
                        Record marketing spend against a campaign, applied across a date range.
                    </p>
                </div>
            </div>

            {/* ── Entry panel ─────────────────────────────────── */}
            <div className="mkt__panel">
                <h2 className="mkt__panel-title">Apply spend to a date range</h2>
                <p className="mkt__panel-hint">
                    Writes one entry per day in the range. Days that already have a value for this campaign are
                    updated, and the other column (actual vs. estimated) is left as it was.
                </p>

                <div className="mkt__controls">
                    <label className="mkt__field mkt__field--grow">
                        <span>Campaign</span>
                        <select
                            value={entry.campaignId}
                            onChange={(e) => setEntry((prev) => ({ ...prev, campaignId: e.target.value }))}
                        >
                            <option value="">Select a campaign…</option>
                            {campaignGroups.map((group) => (
                                <optgroup key={group.vendor} label={group.vendor}>
                                    {group.campaigns.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                    </label>

                    <div className="mkt__field">
                        <span>Dates</span>
                        <DateRangePicker value={entryRange} onChange={setEntryRange} />
                    </div>

                    <label className="mkt__check">
                        <input
                            type="checkbox"
                            checked={entry.skipWeekends}
                            onChange={(e) => setEntry((prev) => ({ ...prev, skipWeekends: e.target.checked }))}
                        />
                        Skip weekends
                    </label>
                </div>

                <div className="mkt__controls">
                    <label className="mkt__field">
                        <span>Amount</span>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={entry.amount}
                            onChange={(e) => setEntry((prev) => ({ ...prev, amount: e.target.value }))}
                            placeholder="0.00"
                        />
                    </label>

                    <div className="mkt__field">
                        <span>Amount is</span>
                        <div className="mkt__segmented">
                            <button
                                type="button"
                                className={entry.mode === 'total' ? 'mkt__segmented--on' : ''}
                                onClick={() => setEntry((prev) => ({ ...prev, mode: 'total' }))}
                            >
                                Total for range
                            </button>
                            <button
                                type="button"
                                className={entry.mode === 'per_day' ? 'mkt__segmented--on' : ''}
                                onClick={() => setEntry((prev) => ({ ...prev, mode: 'per_day' }))}
                            >
                                Per day
                            </button>
                        </div>
                    </div>

                    <div className="mkt__field">
                        <span>Apply to</span>
                        <div className="mkt__segmented">
                            <button
                                type="button"
                                className={entry.field === 'amount' ? 'mkt__segmented--on' : ''}
                                onClick={() => setEntry((prev) => ({ ...prev, field: 'amount' }))}
                            >
                                Actual spend
                            </button>
                            <button
                                type="button"
                                className={entry.field === 'estimated_amount' ? 'mkt__segmented--on' : ''}
                                onClick={() => setEntry((prev) => ({ ...prev, field: 'estimated_amount' }))}
                            >
                                Estimated
                            </button>
                        </div>
                    </div>
                </div>

                <div className={`mkt__preview ${preview.error ? 'mkt__preview--warn' : ''}`}>
                    {preview.error ? (
                        <span>{preview.error}</span>
                    ) : preview.amountMissing ? (
                        <span>
                            <strong>{preview.days}</strong> day{preview.days === 1 ? '' : 's'} in range
                            {entry.skipWeekends ? ' (weekends skipped)' : ''} — enter an amount.
                        </span>
                    ) : entry.mode === 'total' ? (
                        <span>
                            <strong>{preview.days}</strong> day{preview.days === 1 ? '' : 's'}
                            {entry.skipWeekends ? ' (weekends skipped)' : ''} × <strong>{formatMoney(preview.perDay)}</strong>
                            {preview.uneven && <> (last day {formatMoney(preview.lastDay)})</>} = <strong>{formatMoney(preview.total)}</strong>
                        </span>
                    ) : (
                        <span>
                            <strong>{formatMoney(preview.perDay)}</strong> on each of <strong>{preview.days}</strong> day
                            {preview.days === 1 ? '' : 's'}
                            {entry.skipWeekends ? ' (weekends skipped)' : ''} = <strong>{formatMoney(preview.total)}</strong> total
                        </span>
                    )}
                </div>

                <div className="mkt__controls" style={{ marginTop: 14, marginBottom: 0 }}>
                    <button className="blueButton" onClick={handleApply} disabled={!canApply}>
                        {applying ? 'Applying…' : 'Apply Spend'}
                    </button>
                    <button
                        className="mkt__btn-danger"
                        disabled={!entry.campaignId || applying}
                        onClick={() => { setDialogError(null); setDialog({ mode: 'clear' }); }}
                    >
                        Clear Range
                    </button>
                    {selectedCampaign && (
                        <span className="report__refreshed">
                            {selectedCampaign.vendor_name} · {selectedCampaign.type || 'no type'}
                        </span>
                    )}
                </div>
                {selectedCampaign?.ad_platform && entry.field === 'amount' && (
                    <p className="mkt__preview mkt__preview--warn" style={{ marginTop: 12 }}>
                        This campaign's actual spend is synced from {SOURCE_LABEL[selectedCampaign.ad_platform] || selectedCampaign.ad_platform} every
                        night. Actual amounts entered here for the last couple of weeks, or any range a backfill covers, will be
                        replaced by the platform's numbers. Estimates are not affected.
                    </p>
                )}
            </div>

            {notice && <p className="mkt__success">{notice}</p>}
            {error && <p className="mkt__error">{error}</p>}

            {/* ── Recorded spend ──────────────────────────────── */}
            <div className="mkt__controls">
                <label className="mkt__field mkt__field--grow">
                    <span>Show campaign</span>
                    <select
                        value={filter.campaignId}
                        onChange={(e) => setFilter((f) => ({ ...f, campaignId: e.target.value }))}
                    >
                        <option value="">All campaigns</option>
                        {campaignGroups.map((group) => (
                            <optgroup key={group.vendor} label={group.vendor}>
                                {group.campaigns.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                </label>

                <div className="mkt__field">
                    <span>Dates</span>
                    <DateRangePicker
                        value={filter}
                        onChange={(r) => setFilter((f) => ({ ...f, ...r }))}
                    />
                </div>

                <button className="mkt__btn-secondary" onClick={loadSpends} disabled={loading}>
                    {loading ? 'Loading…' : 'Refresh'}
                </button>

                {spends.length > 0 && (
                    <span className="report__refreshed">
                        {spends.length} day{spends.length === 1 ? '' : 's'} · {formatMoney(totals.actual)} actual
                        {' · '}{formatMoney(totals.estimated)} estimated
                    </span>
                )}
            </div>

            {!loading && spends.length === 0 && (
                <p className="page__subtitle">No spend recorded for this range.</p>
            )}

            {spends.length > 0 && (
                <DataGrid
                    rowData={spends}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    pinnedBottomRowData={pinnedBottomRowData}
                    height="calc(100vh - 560px)"
                    enableExport
                    exportFileName={() => `Spends_${filter.startDate}_to_${filter.endDate}`}
                />
            )}

            {dialog && dialog.mode === 'edit' && (
                <Modal
                    title={`Edit ${dialog.spend.spend_date}`}
                    onClose={closeDialog}
                    onSubmit={handleDialogSubmit}
                    submitLabel="Save Changes"
                    submitting={saving}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        {dialog.spend.campaign_name} · {dialog.spend.vendor_name}
                    </p>
                    {dialog.spend.source && (
                        <p className="mkt__preview mkt__preview--warn">
                            The actual amount for this day was pulled from {SOURCE_LABEL[dialog.spend.source] || dialog.spend.source} on{' '}
                            {formatSyncedAt(dialog.spend.synced_at)}. If the day is still inside the sync window, the next nightly
                            sync will put the platform's number back.
                        </p>
                    )}
                    <label className="mkt__field">
                        <span>Actual Amount</span>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editForm.amount}
                            onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))}
                            placeholder="Leave blank to clear"
                            autoFocus
                        />
                    </label>
                    <label className="mkt__field">
                        <span>Estimated Amount</span>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editForm.estimated}
                            onChange={(e) => setEditForm((f) => ({ ...f, estimated: e.target.value }))}
                            placeholder="Leave blank to clear"
                        />
                    </label>
                </Modal>
            )}

            {dialog && dialog.mode === 'delete' && (
                <Modal
                    title="Delete Spend Day"
                    onClose={closeDialog}
                    onSubmit={handleDialogSubmit}
                    submitLabel="Delete"
                    submitVariant="danger"
                    submitting={saving}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        Delete the {dialog.spend.spend_date} entry for <strong>{dialog.spend.campaign_name}</strong>?
                        This can't be undone.
                    </p>
                </Modal>
            )}

            {dialog && dialog.mode === 'clear' && (
                <Modal
                    title="Clear Range"
                    onClose={closeDialog}
                    onSubmit={handleDialogSubmit}
                    submitLabel="Clear Range"
                    submitVariant="danger"
                    submitting={saving}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        Delete every spend day for <strong>{selectedCampaign?.name}</strong> between{' '}
                        <strong>{entryRange.startDate}</strong> and <strong>{entryRange.endDate}</strong>?
                    </p>
                    <p className="mkt-modal__text">
                        This removes both actual and estimated amounts on those days, weekends included, and can't be undone.
                    </p>
                </Modal>
            )}
        </div>
    );
}
